import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import { formatDistanceStrict } from "date-fns";
import Modal from "react-modal";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";

import { Case, Specimen, UserContact } from "pwapi";

import { openDocument } from "shared/actions/caseDetailActions";
import { CaseDetailState } from "shared/reducers/caseDetailReducer";
import { fromYYYYMMDD } from "shared/utils/dates";
import { sortSpecimens } from "shared/utils/specimenUtils";
import { specimen_source } from "shared/utils/specimenUtils";

import { BorderlessButton } from "components/customInputs/buttons/Button";
import ApplicationState from "store/ApplicationState";

import PatientSexAge from "../../PatientSexAge";
import Insurers from "../Insurers";

type PatientModalProps = {
  isOpen: boolean;
  caseDetailObject: CaseDetailState;
  setPatientModal: Function;
};

const Clinician = ({
  ordering_doctor,
  patient,
}: {
  ordering_doctor?: UserContact;
  patient?: CaseDetailState["patient"];
}) => {
  return (
    <>
      {ordering_doctor?.first_name}&nbsp;
      {ordering_doctor?.last_name}
      {patient?.clinic?.name && (
        <span>
          ,&nbsp;
          {patient?.clinic?.name}
        </span>
      )}
    </>
  );
};

const PatientModal = ({
  isOpen,
  caseDetailObject,
  setPatientModal,
}: PatientModalProps) => {
  const dispatch = useDispatch();
  const features = useSelector(({ sys }: ApplicationState) => sys.features);

  const caseMatches = !!caseDetailObject.patient_matches?.length
    ? [...caseDetailObject.patient_matches, caseDetailObject].sort(
        (a, b) =>
          (a.created_at ? Date.parse(a.created_at) : Date.now()) -
          (b.created_at ? Date.parse(b.created_at) : Date.now())
      )
    : [];

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={() => setPatientModal(false)}
      contentLabel="Patient Information"
      className="Modal"
      overlayClassName="Overlay"
    >
      <h4>
        {caseDetailObject.patient.last_name},{" "}
        {caseDetailObject.patient.first_name}{" "}
        <span style={{ opacity: 0.7 }}>
          (
          <PatientSexAge
            patient={caseDetailObject.patient}
            shouldDisplayDOB={false}
          />
          )
        </span>
      </h4>
      <dl>
        {caseDetailObject.patient?.date_of_birth && (
          <>
            <dt>DOB</dt>
            <dd>{fromYYYYMMDD(caseDetailObject.patient.date_of_birth)}</dd>
          </>
        )}
        <dt>Clinician</dt>
        <dd>
          <Clinician
            ordering_doctor={caseDetailObject.ordering_doctor}
            patient={caseDetailObject.patient}
          />
        </dd>
        {(caseDetailObject.self_pay ||
          (caseDetailObject.billing_type as Case.billing_type | "cash") ===
            "cash") && (
          <>
            <dt>Billing</dt>
            <dd>Self Pay</dd>
          </>
        )}
        <Insurers patient={caseDetailObject.patient} />
      </dl>

      <hr />

      {features?.patientHistory && !!caseMatches?.length && (
        <div className="PatientMatches">
          <p>
            <strong>Potential Matches</strong>
          </p>
          <table>
            <tbody>
              {caseMatches.map((patientCase) => (
                <tr
                  key={patientCase.id}
                  className={classNames({
                    PatientMatches__on: patientCase.id === caseDetailObject.id,
                  })}
                >
                  <td>
                    {patientCase.id !== caseDetailObject.id ? (
                      <Link to={`/cases/${patientCase.id}`} target="_blank">
                        <strong>{patientCase.accession}</strong>
                      </Link>
                    ) : (
                      <strong>{patientCase.accession}</strong>
                    )}
                    <br />
                    <small>{patientCase.status}</small>
                  </td>
                  <td>
                    {patientCase.ordering_doctor?.first_name}&nbsp;
                    {patientCase.ordering_doctor?.last_name}
                    <br />
                    <small>{patientCase.patient?.clinic?.name}</small>
                  </td>
                  <td>
                    {sortSpecimens(patientCase.specimens as Specimen[]).map(
                      (s: Specimen) => (
                        <div key={s.id}>
                          <code>
                            <strong>{s.name}</strong>
                          </code>
                          &mdash;
                          {/* TODO: the enum of Specimen.procedure is incompatible with the union type on Case.specimens */}
                          {specimen_source(s)}
                        </div>
                      )
                    )}
                  </td>
                  <td>
                    {patientCase.id === caseDetailObject.id ? (
                      "currently viewing"
                    ) : (
                      <>
                        {patientCase.id !== caseDetailObject.id &&
                          patientCase.created_at &&
                          caseDetailObject.created_at && (
                            <>
                              {formatDistanceStrict(
                                Date.parse(patientCase.created_at),
                                Date.parse(caseDetailObject.created_at)
                              )}{" "}
                              {patientCase.created_at <=
                              caseDetailObject.created_at
                                ? "before"
                                : "later"}
                            </>
                          )}
                      </>
                    )}

                    {patientCase.finalized_report_id && (
                      <div>
                        <BorderlessButton
                          title="View Report"
                          size="small"
                          onClick={() => {
                            patientCase.id &&
                              patientCase.finalized_report_id &&
                              dispatch(
                                openDocument(
                                  patientCase.id,
                                  patientCase.finalized_report_id
                                )
                              );
                          }}
                        >
                          <>
                            Report &nbsp;
                            <FontAwesomeIcon
                              icon="file-export"
                              style={{ color: "var(--blue)" }}
                            />
                          </>
                        </BorderlessButton>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Modal>
  );
};

export default PatientModal;
